/**
 * 🎀 MASTER RIBBON — Componente Genérico Unificado
 * 
 * Renderiza qualquer RibbonConfig recebido via props.
 * Sub-componentes extraídos: ToolBtn, Separator, GroupRenderer.
 * 
 * Layout: glass-action-ribbon, h-12, flex items-center
 * Posição fixa: left → center → right (preserva memória muscular)
 * 
 * Substitui: Ribbon.tsx, ActionRibbon.tsx, GainsRibbon.tsx, WorkflowRibbon (inline)
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import type { MasterRibbonProps, RibbonAction, RibbonGroup, RibbonDropdownItem } from '../types/ribbon';
import {
  activeDesktopRibbonIconClass,
  activeDesktopRibbonLabelClass,
  desktopRibbonButtonClass,
  desktopRibbonDividerClass,
  desktopRibbonGroupClass,
  desktopRibbonIconClass,
  desktopRibbonLabelClass,
} from './ribbon/DesktopRibbonControls';

// ─── Separator ─────────────────────────────────────────────

const Separator: React.FC = () => (
  <div className={desktopRibbonDividerClass} />
);

// ─── ToolBtn (átomo) ───────────────────────────────────────

interface ToolBtnProps {
  action: RibbonAction;
}

const ToolBtn: React.FC<ToolBtnProps> = ({ action }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const handleDropdownToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    setShowDropdown(prev => !prev);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handleClick = () => setShowDropdown(false);
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowDropdown(false); };
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [showDropdown]);

  if (action.hidden) return null;

  const Icon = action.icon;
  const hasDropdown = action.dropdown && action.dropdown.length > 0;

  const iconNode = <Icon size={18} strokeWidth={1.5} className="icon-crisp shrink-0" />;
  const buttonClass = `${desktopRibbonButtonClass} ${action.disabled ? '' : 'cursor-pointer'}`;

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={hasDropdown ? handleDropdownToggle : action.onClick}
        disabled={action.disabled}
        title={action.tooltip || action.label}
        className={buttonClass}
      >
        <span className={action.active ? activeDesktopRibbonIconClass : desktopRibbonIconClass}>{iconNode}</span>
        <span className={action.active ? activeDesktopRibbonLabelClass : desktopRibbonLabelClass}>{action.label}</span>
        {hasDropdown && (
          <ChevronDown
            size={8}
            className="pointer-events-none absolute right-1 top-1 text-[#6B7280] opacity-80"
          />
        )}
        {action.badge != null && (
          <span className="pointer-events-none absolute right-0 top-0 flex h-[16px] min-w-[16px] translate-x-[15%] translate-y-[-10%] items-center justify-center rounded-full bg-red-500 px-1 text-[8px] font-bold leading-none text-white">
            {action.badge}
          </span>
        )}
      </button>

      {/* Dropdown Portal */}
      {hasDropdown && showDropdown && createPortal(
        <div
          className="fixed z-[9999] min-w-[180px] rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] py-1 text-[var(--text-primary)]"
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
          onClick={e => e.stopPropagation()}
        >
          {action.dropdown!.map((item) => (
            <React.Fragment key={item.id}>
              {item.divider ? (
                <div className="my-1 h-px bg-[var(--border-subtle)]" />
              ) : (
                <button
                  onClick={() => { item.onClick(); setShowDropdown(false); }}
                  disabled={item.disabled}
                  className="flex h-8 w-full items-center gap-2 px-3 text-[11px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)] disabled:opacity-40"
                >
                  {item.icon && <item.icon size={14} className="shrink-0" />}
                  <span>{item.label}</span>
                  {item.description && <span className="ml-auto text-[10px] text-[var(--text-secondary)]">{item.description}</span>}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

// ─── GroupRenderer (molécula) ──────────────────────────────

interface GroupRendererProps {
  group: RibbonGroup;
  showSeparatorAfter?: boolean;
}

const GroupRenderer: React.FC<GroupRendererProps> = ({ group, showSeparatorAfter }) => {
  if (group.hidden) return null;

  const visibleActions = group.actions.filter(a => !a.hidden);
  if (visibleActions.length === 0) return null;

  return (
    <>
      <div
        className="flex h-full shrink-0 items-center"
        style={{
          paddingRight: showSeparatorAfter ? '8px' : undefined,
          borderRight: showSeparatorAfter ? '1px solid var(--border-default, rgba(255,255,255,0.08))' : undefined,
          marginRight: showSeparatorAfter ? '4px' : undefined,
        }}
      >
        <div className={desktopRibbonGroupClass}>
          {visibleActions.map(action => (
            <ToolBtn key={action.id} action={action} />
          ))}
        </div>
      </div>
    </>
  );
};

// ─── MasterRibbon (organismo) ──────────────────────────────

export const MasterRibbon: React.FC<MasterRibbonProps> = ({ config, className = '' }) => {
  const leftGroups = config.groups.filter(g => g.position === 'left' && !g.hidden);
  const centerGroups = config.groups.filter(g => g.position === 'center' && !g.hidden);
  const rightGroups = config.groups.filter(g => g.position === 'right' && !g.hidden);

  return (
    <div
      data-ribbon="true"
      data-module={config.moduleId}
      className={`w-full enterprise-ribbon-zone officebar-surface relative shrink-0 select-none text-[var(--ribbon-text-primary)] ${className}`}
      style={{ zIndex: 30, minHeight: 'var(--shell-ribbon-height, 42px)', background: 'var(--officebar-bg)', backgroundColor: 'var(--officebar-bg)', backgroundImage: 'none' }}
    >
      <div
        className="flex items-center py-0"
        style={{
          height: 'var(--shell-ribbon-height, 46px)',
          paddingLeft: 'var(--shell-content-inset, 10px)',
          paddingRight: 'var(--shell-content-inset, 10px)',
          gap: '6px',
          flexWrap: 'nowrap',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        {/* Slot: Before Groups */}
        {config.slots?.beforeGroups}

        {/* LEFT — Arquivo, Exportar (posição fixa) */}
        <div className="flex shrink-0 items-center h-full" style={{ gap: '6px' }}>
          {leftGroups.map((group, i) => (
            <GroupRenderer
              key={group.id}
              group={group}
              showSeparatorAfter={i < leftGroups.length - 1 || centerGroups.length > 0}
            />
          ))}
        </div>

        {/* CENTER — Edição, Visualização (compacto, colado à LEFT) */}
        <div className="flex shrink-0 items-center h-full" style={{ gap: '6px' }}>
          {config.slots?.centerWidget}
          {centerGroups.map((group, i) => (
            <GroupRenderer
              key={group.id}
              group={group}
              showSeparatorAfter={i < centerGroups.length - 1}
            />
          ))}
        </div>

        {/* RIGHT — Análise, Ferramentas */}
        <div className="ml-auto flex shrink-0 items-center justify-end h-full pr-1" style={{ gap: '8px' }}>
          {rightGroups.map((group, i) => (
            <GroupRenderer
              key={group.id}
              group={group}
              showSeparatorAfter={i < rightGroups.length - 1}
            />
          ))}
        </div>

        {/* Slot: After Groups */}
        {config.slots?.afterGroups}
      </div>
    </div>
  );
};

export default MasterRibbon;
